import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Header } from "./Header";

const STEPS = [
  "Finding tonight's live events…",
  "Scouting dinner spots nearby…",
  "Lining up drinks for after…",
  "Asking our concierge for a hot take…",
];

function SkeletonCard({ index }) {
  return (
    <div
      data-testid={`skeleton-card-${index}`}
      className="rounded-2xl border border-border bg-card p-6 sm:p-7"
      style={{ animationDelay: `${index * 120}ms` }}
    >
      <div className="flex items-start gap-5">
        <span className="h-11 w-11 shrink-0 rounded-full shimmer" />
        <div className="flex-1 space-y-3">
          <span className="block h-3 w-40 rounded-full shimmer" />
          <span className="block h-7 w-3/4 rounded-lg shimmer" />
          {index === 1 && <span className="block h-40 w-full rounded-xl shimmer" />}
          <span className="block h-3 w-2/3 rounded-full shimmer" />
          <span className="block h-14 w-full rounded-xl shimmer" />
          <span className="block h-8 w-32 rounded-full shimmer" />
        </div>
      </div>
    </div>
  );
}

export function ItinerarySkeleton({ city }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    window.scrollTo({ top: 0 });
    const id = setInterval(() => setStep((s) => (s + 1) % STEPS.length), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="itinerary-skeleton">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="mb-3 block h-3 w-20 rounded-full shimmer" />
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              Building a night in {city}
            </h1>
            <motion.p
              key={step}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="skeleton-status"
            >
              <Loader2 size={14} className="animate-spin neon-magenta" /> {STEPS[step]}
            </motion.p>
          </div>
          <span className="h-10 w-48 rounded-full shimmer" />
        </div>

        <div className="mt-8 flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div className="order-1 lg:order-2 w-full lg:w-1/2">
            <div className="h-[360px] lg:h-[calc(100vh-140px)] lg:sticky lg:top-24 rounded-2xl overflow-hidden border border-border shimmer relative">
              <div className="absolute inset-0 grid-noise opacity-40" />
              {[
                ["pin-a", "30%", "35%"],
                ["pin-b", "55%", "58%"],
                ["pin-c", "70%", "28%"],
              ].map(([id, top, left], i) => (
                <span
                  key={id}
                  className="absolute h-9 w-9 rounded-full bg-primary/40 animate-pulse"
                  style={{ top, left, animationDelay: `${i * 200}ms` }}
                />
              ))}
            </div>
          </div>
          <div className="order-2 lg:order-1 w-full lg:w-1/2 flex flex-col gap-6">
            {[0, 1, 2].map((i) => (
              <SkeletonCard key={i} index={i} />
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
